import { useEffect, useMemo } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';

import type { agentAri_activationQuery } from './__generated__/agentAri_activationQuery.graphql.ts';

type UseResolveAgentAriParams = {
	/** Non-ARI Agent ID uuid format. Typically comes from the REST API. */
	agentId: string;
	/**
	 * Pre-resolved agent ARI. When provided, the activation query is skipped
	 * and this value is returned directly.
	 */
	existingAgentAri?: string | null;
	cloudId: string;
};

type UseResolveAgentAriResult = {
	/** The resolved agent ARI, or null if it could not be constructed. */
	agentAri: string | null;
};

/**
 * Resolves an agent ARI, either returning a pre-resolved value or fetching the activation ID to construct one.
 *
 * If `existingAgentAri` is provided, it is returned directly (the activation query is skipped).
 * Otherwise, fetches the Rovo activation ID for the given cloudId and constructs the ARI as:
 *   `ari:cloud:rovo::agent/activation/${activationId}/${agentId}`
 *
 * Must be used within a Relay environment provider and Suspense boundary.
 */
export const useResolveAgentAri = ({
	agentId,
	existingAgentAri,
	cloudId,
}: UseResolveAgentAriParams): UseResolveAgentAriResult => {
	di(logException);
	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const activationResult = useLazyLoadQuery<agentAri_activationQuery>(
		graphql`
			query agentAri_activationQuery($cloudId: ID!, $product: String!, $skip: Boolean!) {
				tenantContexts(cloudIds: [$cloudId]) @skip(if: $skip) {
					activationIdByProduct(product: $product) {
						active
					}
				}
			}
		`,
		{
			cloudId,
			product: 'rovo',
			skip: Boolean(existingAgentAri),
		},
	);

	const agentAri = useMemo(() => {
		if (existingAgentAri) {
			return existingAgentAri;
		}
		const activationId = activationResult?.tenantContexts?.[0]?.activationIdByProduct?.active;
		return activationId ? `ari:cloud:rovo::agent/activation/${activationId}/${agentId}` : null;
	}, [existingAgentAri, activationResult, agentId]);

	const hasAgentAri = Boolean(agentAri);

	useEffect(() => {
		if (!hasAgentAri && !existingAgentAri) {
			logException({
				exception: new Error('useResolveAgentAri: failed to resolve activation ID'),
				name: 'useResolveAgentAri_activationQuery',
				tags: {
					cloudId,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				},
			});
		}
	}, [hasAgentAri, existingAgentAri, cloudId]);

	return { agentAri };
};
